/**
 * PositiveEVWidget — +EV opportunity finder
 * Devigs Pinnacle lines, compares against all other books, surfaces +EV bets
 * Prefix: pev-
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import { adaptEngineEvs, fetchCrossbookEdge, getEdgeFeedProof } from '../lib/edgeData'
import { getBookLogo } from '../lib/logoMaps'
import { getLeagueLogoPath } from '../lib/leagueLogos'
import { RefreshCw, Zap, TrendingUp, ArrowUpRight, Filter, ChevronDown, ChevronUp } from 'lucide-react'
import './PositiveEVWidget.css'

const LEAGUES = [
  { id: 'ALL', label: 'All' },
  { id: 'NBA', label: 'NBA' },
  { id: 'MLB', label: 'MLB' },
  { id: 'NHL', label: 'NHL' },
  { id: 'NFL', label: 'NFL' },
  { id: 'NCAAB', label: 'MCBB' },
  { id: 'NCAAW', label: 'WCBB' },
  { id: 'SOCCER', label: 'Soccer' },
  { id: 'MMA', label: 'MMA' },
]

const MARKETS = [
  { id: 'ALL', label: 'All' },
  { id: 'ML', label: 'ML' },
  { id: 'SPREAD', label: 'Spread' },
  { id: 'TOTAL', label: 'Total' },
]

const MIN_EV_OPTIONS = [0, 1, 2, 3, 5, 10]

const REFRESH_MS = 30000

function formatOdds(american) {
  if (american == null) return '—'
  return american > 0 ? `+${american}` : `${american}`
}

function formatStartTime(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const now = new Date()
  const isToday = d.toDateString() === now.toDateString()
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  if (isToday) return time
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  if (d.toDateString() === tomorrow.toDateString()) return `Tom ${time}`
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${time}`
}

export default function PositiveEVWidget() {
  const [engineRows, setEngineRows] = useState([])
  const [proof, setProof] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [league, setLeague] = useState('ALL')
  const [market, setMarket] = useState('ALL')
  const [minEV, setMinEV] = useState(0)
  const [sortCol, setSortCol] = useState('ev')
  const [sortDir, setSortDir] = useState('desc')

  const fetchOdds = useCallback(async () => {
    try {
      setLoading(true)
      const payload = await fetchCrossbookEdge()
      setEngineRows(adaptEngineEvs(payload))
      setProof(getEdgeFeedProof(payload))
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchOdds()
    const interval = setInterval(fetchOdds, REFRESH_MS)
    return () => clearInterval(interval)
  }, [fetchOdds])

  // Filtering and sorting happen in the browser; fair-value and EV math come
  // from the versioned cross-book engine so the UI cannot invent a second result.
  const opportunities = useMemo(() => {
    return engineRows.filter((row) => {
      if (league !== 'ALL' && row.league !== league) return false
      if (market !== 'ALL' && row.market !== market) return false
      return row.ev >= minEV
    })
  }, [engineRows, league, market, minEV])

  // Sort
  const sorted = useMemo(() => {
    const arr = [...opportunities]
    arr.sort((a, b) => {
      let cmp = 0
      switch (sortCol) {
        case 'ev': cmp = a.ev - b.ev; break
        case 'edge': cmp = (a.edge || 0) - (b.edge || 0); break
        case 'book': cmp = a.book.localeCompare(b.book); break
        case 'bookOdds': cmp = (a.bookOdds || 0) - (b.bookOdds || 0); break
        case 'hold': cmp = (a.hold || 99) - (b.hold || 99); break
        case 'start': cmp = new Date(a.start_time) - new Date(b.start_time); break
        default: cmp = a.ev - b.ev
      }
      return sortDir === 'desc' ? -cmp : cmp
    })
    return arr
  }, [opportunities, sortCol, sortDir])

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setSortCol(col); setSortDir('desc') }
  }

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return null
    return sortDir === 'desc' ? <ChevronDown size={10} /> : <ChevronUp size={10} />
  }

  // Count opportunities per league for filter badges
  const leagueCounts = useMemo(() => {
    const counts = {}
    for (const opp of opportunities) {
      const lg = opp.league || 'OTHER'
      counts[lg] = (counts[lg] || 0) + 1
    }
    return counts
  }, [opportunities])

  // Count per market
  const marketCountsForFilter = useMemo(() => {
    const counts = { ALL: opportunities.length, ML: 0, SPREAD: 0, TOTAL: 0 }
    for (const opp of opportunities) {
      const m = opp.market
      if (counts[m] != null) counts[m]++
    }
    return counts
  }, [opportunities])

  // Stats
  const avgEV = sorted.length > 0 ? sorted.reduce((s, r) => s + r.ev, 0) / sorted.length : 0
  const hotCount = sorted.filter(r => r.ev >= 5).length
  const bestEV = sorted.length > 0 ? sorted[0]?.ev : 0
  const proofAgeMs = proof?.capturedAt ? Math.max(0, Date.now() - new Date(proof.capturedAt).getTime()) : null
  const proofIsStale = proofAgeMs == null || proofAgeMs > 5 * 60_000

  return (
    <div className="widget pev-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <Zap size={16} />
          Positive EV
          <span className="pev-count">{sorted.length} opportunities</span>
          {hotCount > 0 && <span className="pev-hot-badge">🔥 {hotCount} hot</span>}
        </h3>
        <div className="pev-header-actions">
          {bestEV > 0 && <span className="pev-best-badge">Best: +{bestEV.toFixed(1)}%</span>}
          <span className="pev-avg">Avg: +{avgEV.toFixed(1)}%</span>
          <button className="widget-refresh-btn" onClick={fetchOdds} title="Refresh">
            <RefreshCw size={14} className={loading ? 'pev-spinner' : ''} />
          </button>
        </div>
      </div>

      {proof && (
        <div className={`pev-source-proof ${proofIsStale ? 'is-stale' : 'is-live'}`}>
          <span>Cross-book edge engine</span>
          <span>{proof.books} books</span>
          <span>{proof.markets} married markets</span>
          <span>Captured {new Date(proof.capturedAt).toLocaleString()}</span>
          <span>{Math.round(proofAgeMs / 60_000)}m old</span>
        </div>
      )}

      {/* Filters */}
      <div className="pev-filters">
        <div className="pev-filter-row">
          {LEAGUES.map(l => {
            const cnt = l.id === 'ALL' ? opportunities.length : (leagueCounts[l.id] || 0)
            // Hide leagues with zero opportunities (except ALL)
            if (l.id !== 'ALL' && cnt === 0 && league !== l.id) return null
            return (
              <button
                key={l.id}
                className={`pev-pill ${league === l.id ? 'active' : ''}`}
                onClick={() => setLeague(l.id)}
              >
                {getLeagueLogoPath(l.id) && (
                  <img src={getLeagueLogoPath(l.id)} alt="" className="pev-pill-logo"
                    onError={e => { e.target.style.display = 'none' }} />
                )}
                {l.label}
                {cnt > 0 && <span className="pev-pill-count">{cnt}</span>}
              </button>
            )
          })}
        </div>
        <div className="pev-filter-row">
          {MARKETS.map(m => (
            <button key={m.id} className={`pev-pill ${market === m.id ? 'active' : ''}`}
              onClick={() => setMarket(m.id)}>
              {m.label}
              {marketCountsForFilter[m.id] > 0 && <span className="pev-pill-count">{marketCountsForFilter[m.id]}</span>}
            </button>
          ))}
          <span className="pev-filter-sep">|</span>
          <span className="pev-filter-label">Min EV:</span>
          {MIN_EV_OPTIONS.map(v => (
            <button key={v} className={`pev-pill pev-ev-pill ${minEV === v ? 'active' : ''}`}
              onClick={() => setMinEV(v)}>{v}%</button>
          ))}
        </div>
      </div>

      {error && <div className="pev-error">{error}</div>}

      {loading && engineRows.length === 0 ? (
        <div className="pev-loading">Scanning for +EV opportunities...</div>
      ) : sorted.length === 0 ? (
        <div className="pev-empty">
          <Zap size={32} />
          <p>No +EV opportunities found with current filters</p>
          <p className="pev-hint">{minEV > 0 ? 'Try lowering the minimum EV%' : 'No engine-qualified positive EV offers in the current capture'}</p>
        </div>
      ) : (
        <div className="pev-table-wrap">
          <table className="pev-table">
            <thead>
              <tr>
                <th className="pev-th pev-th-ev" onClick={() => handleSort('ev')}>EV% <SortIcon col="ev" /></th>
                <th className="pev-th pev-th-edge" onClick={() => handleSort('edge')}>Edge <SortIcon col="edge" /></th>
                <th className="pev-th pev-th-game">Game</th>
                <th className="pev-th pev-th-market">Market</th>
                <th className="pev-th pev-th-sel">Selection</th>
                <th className="pev-th pev-th-odds" onClick={() => handleSort('bookOdds')}>Book Odds <SortIcon col="bookOdds" /></th>
                <th className="pev-th pev-th-fair">Fair Odds</th>
                <th className="pev-th pev-th-book" onClick={() => handleSort('book')}>Book <SortIcon col="book" /></th>
                <th className="pev-th pev-th-pinn">Pinnacle</th>
                <th className="pev-th pev-th-hold" onClick={() => handleSort('hold')}>Hold% <SortIcon col="hold" /></th>
                <th className="pev-th pev-th-start" onClick={() => handleSort('start')}>Start <SortIcon col="start" /></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((opp, i) => (
                <tr key={i} className={`pev-row ${opp.ev >= 5 ? 'pev-row-hot' : opp.ev >= 2 ? 'pev-row-good' : ''} ${opp.is_live ? 'pev-row-live' : ''}`}>
                  <td className="pev-td pev-td-ev">
                    <span className={`pev-ev-badge ${opp.ev >= 5 ? 'hot' : opp.ev >= 2 ? 'good' : ''}`}>
                      +{opp.ev.toFixed(1)}%
                    </span>
                  </td>
                  <td className="pev-td pev-td-edge pev-mono">
                    {opp.edge != null ? (opp.edge > 0 ? `+${opp.edge}` : opp.edge) : '—'}
                  </td>
                  <td className="pev-td pev-td-game">
                    <div className="pev-game-info">
                      {getLeagueLogoPath(opp.league) && (
                        <img src={getLeagueLogoPath(opp.league)} alt="" className="pev-league-logo"
                          onError={e => { e.target.style.display = 'none' }} />
                      )}
                      <span className="pev-game-text">{opp.game}</span>
                    </div>
                  </td>
                  <td className="pev-td pev-td-market">
                    <span className="pev-market-badge">
                      {opp.market}{opp.handicap != null ? ` ${opp.handicap > 0 ? '+' : ''}${opp.handicap}` : ''}
                    </span>
                  </td>
                  <td className="pev-td pev-td-sel">{opp.selection}</td>
                  <td className="pev-td pev-td-odds pev-mono pev-green">{formatOdds(opp.bookOdds)}</td>
                  <td className="pev-td pev-td-fair pev-mono pev-dim">{formatOdds(opp.fairOdds)}</td>
                  <td className="pev-td pev-td-book">
                    <div className="pev-book-info">
                      {getBookLogo(opp.book) && (
                        <img src={getBookLogo(opp.book)} alt="" className="pev-book-logo"
                          onError={e => { e.target.style.display = 'none' }} />
                      )}
                      <span>{opp.book}</span>
                    </div>
                  </td>
                  <td className="pev-td pev-td-pinn pev-mono">{formatOdds(opp.pinnacleOdds)}</td>
                  <td className="pev-td pev-td-hold">
                    {opp.hold != null ? (
                      <span className={`pev-hold ${opp.hold < 3 ? 'pev-hold-low' : opp.hold > 8 ? 'pev-hold-high' : ''}`}>
                        {opp.hold.toFixed(1)}%
                      </span>
                    ) : '—'}
                  </td>
                  <td className="pev-td pev-td-start">{opp.start_label || formatStartTime(opp.start_time)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
